import axiosInstance from './axiosInstance';

export interface PlatformSetting {
  id: number;
  name: string;       // "THEME.DARK.BODY_COLOR"
  cat: string;        // "THEME.DARK"
  value: string | boolean;
  dataType: 'color' | 'string' | 'bool' | 'select';
}

export interface MaintenanceInfo {
  active: boolean;
  message?: string;
}

export interface ParsedPlatformSettings {
  themes: {
    dark: Record<string, string>;
    light: Record<string, string>;
  };
  staticColors: Record<string, string>;
  branding: Record<string, string>;
  typography: Record<string, string>;
  dimensions: Record<string, string>;
  features: Record<string, boolean | string>;
  maintenance: Record<string, MaintenanceInfo>;
}

// Parse the flat API response into a structured object
export const parsePlatformSettings = (settings: PlatformSetting[]): ParsedPlatformSettings => {
  const result: ParsedPlatformSettings = {
    themes: { dark: {}, light: {} },
    staticColors: {},
    branding: {},
    typography: {},
    dimensions: {},
    features: {},
    maintenance: {},
  };

  settings.forEach((setting) => {
    const parts = setting.name.split('.');

    if (parts[0] === 'THEME') {
      const theme = parts[1].toLowerCase() as 'dark' | 'light';
      const key = parts[2];
      if (result.themes[theme] && typeof setting.value === 'string') {
        result.themes[theme][key] = setting.value;
      }
    } else if (parts[0] === 'STATIC_COLORS') {
      const key = parts[1];
      if (typeof setting.value === 'string') {
        result.staticColors[key] = setting.value;
      }
    } else if (parts[0] === 'BRANDING') {
      const key = parts[1];
      if (typeof setting.value === 'string') {
        result.branding[key] = setting.value;
      }
    } else if (parts[0] === 'TYPOGRAPHY') {
      const key = parts[1];
      if (typeof setting.value === 'string') {
        result.typography[key] = setting.value;
      }
    } else if (parts[0] === 'DIMENSIONS') {
      const key = parts[1];
      if (typeof setting.value === 'string') {
        result.dimensions[key] = setting.value;
      }
    } else if (parts[0] === 'FEATURES') {
      const key = parts[1];
      result.features[key] = setting.value;
    } else if (parts[0] === 'MAINTENANCE') {
      const feature = parts[1]; // e.g., EXCHANGE, DCA, etc.
      const subKey = parts[2];  // e.g., MESSAGE (optional)
      
      // Initialize the maintenance info if not exists
      if (!result.maintenance[feature]) {
        result.maintenance[feature] = { active: false };
      }
      
      if (subKey === 'MESSAGE' && typeof setting.value === 'string') {
        result.maintenance[feature].message = setting.value;
      } else if (!subKey) {
        // MAINTENANCE.EXCHANGE = true/false
        result.maintenance[feature].active = setting.value === true || setting.value === 'true';
      }
    }
  });

  return result;
};

export const PlatformSettingsService = {
  async getSettings(): Promise<PlatformSetting[]> {
    const response = await axiosInstance.get('/app/platform-settings');
    return response.data;
  },
};
